import { useState } from "react";
import { Link } from "react-router-dom";
import Callout from "components/callout/callout";
import { AccessRole, ProductAction, FormStatus } from "types";
import { useTranslation } from "react-i18next";
import DateTime from "components/text/DateTime";
import ActionBadge from "components/action/actionBadge";
import useLayout from "hooks/useLayout";
import useActions from "hooks/useActions";
import LoadMore from "components/list/loadMore";
import useProducts from "hooks/useProducts";
import Section from "components/layout/section";
import IconImage from "components/image/IconImage";
import { ReactComponent as ActionsIcon } from "assets/icons/actions.svg";
import { ReactComponent as AddIcon } from "assets/icons/add.svg";
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg";
import { ReactComponent as HistoryIcon } from "assets/icons/restore.svg";
import IconButton from "components/buttons/iconButton";
import FloatingButtonGroup from "components/buttons/floatingButtonGroup";
import FloatingButtonGroupButton from "components/buttons/FloatingButtonGroupButton";
import { ReactComponent as ArrowDropDownIcon } from "assets/icons/arrow_drop_down.svg";
import DocList, { ViewMode } from "components/list/docList/docList";
import TableListRow from "components/list/tableList/tableListRow";
import TableListCell from "components/list/tableList/tableListCell";
import TableList from "components/list/tableList/tableList";
import Button from "components/inputs/button";
import Avatar from "components/user/Avatar";
import UserCard from "components/user/UserCard";
import ActionFilter from "./actionFilter";
import Tag from "components/tags/tag";
import Image from "components/image/Image";
import { ReactComponent as ImageIcon } from "assets/icons/image.svg";
import useStorages from "@/hooks/useStorages";
import useAccess from "@/hooks/useAccess";


interface ActionsListProps {
    storageId: string;
    productId?: string;
    actionPaths?: string[];
    displayHelper?: boolean;
    transactionId?: string;
}

const ActionsList: React.FC<ActionsListProps> = ({ storageId, productId, actionPaths, displayHelper = true, transactionId }) => {
    const { t } = useTranslation();

    // Component state.
    const [focusProductAction, setFocusProductAction] = useState<ProductAction | undefined>(undefined);

    const {
        openDialog
    } = useLayout();

    const {
        actions,
        filteredActions,
        actionsStatus,
        fetchNextPage,
        hasNextPage,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        typeFilter,
        setTypeFilter
    } = useActions({ storageId, productId, actionPaths, transactionId });

    const {
        product,
        products
    } = useProducts({ storageId, productId });

    const {
        isTransactionsEnabled,
        isActionsRequiredToBeLinkedToTransactions,
        isStockAdjustmentEnabled
    } = useStorages({ storageId });

    const {
        limitToRoles
    } = useAccess({ storageId });

    // Helper function to get product info for an action
    const getProductInfo = (action: ProductAction) => {
        if (productId && product) {
            // If we're viewing a single product's actions, use that product
            return product;
        } else if (action.ids?.productId) {
            // If viewing actions across products (like in transactions), find the product
            return products.find(p => p.id === action.ids?.productId);
        }
        return null;
    };

    // Open action info dialog.
    const openActionInfoDialog = (action: ProductAction) => {
        setFocusProductAction(action);
        openDialog('show_action', { 
            actionId: action.id,
            storageId: action.ids?.storageId || storageId,
            productId: action.ids?.productId || productId
        });
    };

    const showTransactionColumn = isTransactionsEnabled() && !transactionId

    // Handle errors.
    if (!storageId) return <Callout type={`error`}>{t('noStorageId')}</Callout>;
    if (!productId && !actionPaths) return <Callout type={`error`}>{t('noActions')}</Callout>;

    // Helper/filter section for DocList
    const helperElement = (
        <Section design={`card-centered`}>
            <IconImage>
                <ActionsIcon />
            </IconImage>
            <h2>{t(`titleNoActions`)}</h2>
            <p>{t('actionListNoActions')}</p>
            {limitToRoles([AccessRole.OWNER, AccessRole.EDITOR, AccessRole.WORKER]) && (
            isActionsRequiredToBeLinkedToTransactions() ? (
            <FloatingButtonGroup id={`top-bar-stock-actions-action-list`} openingButton={<Button wideOnMobile design="primary"><AddIcon />{t('titleCreateTransaction')}<ArrowDropDownIcon /></Button>}>
                <FloatingButtonGroupButton to={`/storage/transactions/create/incoming`}>{t('buttonNewIncoming')}</FloatingButtonGroupButton>
                <FloatingButtonGroupButton to={`/storage/transactions/create/outgoing`}>{t('buttonNewOutgoing')}</FloatingButtonGroupButton>
                {isStockAdjustmentEnabled() && (
                    <FloatingButtonGroupButton onClick={() => openDialog(`adjust_product_stock`)}>{t('buttonAdjust')}</FloatingButtonGroupButton>
                )}
            </FloatingButtonGroup>
            ) : (
            <FloatingButtonGroup id={`top-bar-stock-actions-action-list`} openingButton={<Button wideOnMobile design="primary"><AddIcon />{t('buttonNewStockAction')}<ArrowDropDownIcon /></Button>}>
                <FloatingButtonGroupButton onClick={() => openDialog(`add_to_stock`)}>{t('buttonAdd')}</FloatingButtonGroupButton>
                <FloatingButtonGroupButton onClick={() => openDialog(`remove_from_stock`)}>{t('buttonRemove')}</FloatingButtonGroupButton>
                {isStockAdjustmentEnabled() && (
                    <FloatingButtonGroupButton onClick={() => openDialog(`adjust_product_stock`)}>{t('buttonAdjust')}</FloatingButtonGroupButton>
                )}
            </FloatingButtonGroup>
            )
            )}
        </Section>
    )

    if (product && !product.counters?.actionsCount && displayHelper && productId) {
        return helperElement
    }

    // Render the JSX.
    return (
        <>
            <DocList
                id={`action_list`}
                viewModes={[ViewMode.Table]}
                defaultViewMode={ViewMode.Table}
                showResults={(filteredActions.length > 0) || actionsStatus !== FormStatus.SUCCESS}
                filterElement={
                    <ActionFilter
                        filterQuery={filterQuery}
                        setFilterQuery={setFilterQuery}
                        isLoading={actionsStatus === FormStatus.LOADING}
                        allowedSortBy={["createdAt", "amount"]}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        sortDirection={sortDirection}
                        setSortDirection={setSortDirection}
                        typeFilter={typeFilter}
                        setTypeFilter={setTypeFilter}
                    />
                }
                noResultsElement={
                    <Callout type={`error`}>{t(`labelNoResults`)}</Callout>
                }
            >
                <div data-view-mode={ViewMode.Table}>
                    <TableList>
                        <TableListRow isHeader={true}>
                            {actionPaths && <TableListCell>{t('labelProduct')}</TableListCell>}
                            <TableListCell>{t('action')}</TableListCell>
                            <TableListCell>{t('timestamp')}</TableListCell>
                            {showTransactionColumn && <TableListCell>{t('labelTransaction')}</TableListCell>}
                            <TableListCell hideBelowLg>{t('columnUser')}</TableListCell>
                            <TableListCell></TableListCell>
                        </TableListRow>
                        {filteredActions.length > 0
                            ? filteredActions.map((action, key) => {
                                const productInfo = getProductInfo(action);
                                return (
                                    <TableListRow key={key} onClick={() => openActionInfoDialog(action)}>
                                        {actionPaths && (
                                            <TableListCell>
                                                {productInfo ? (
                                                    <Link 
                                                        to={`/storage/products/${action.ids?.productId}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="flex items-center gap-2"
                                                    >
                                                        {productInfo.images && productInfo.images.length > 0 ? (
                                                            <div className="max-sm:hidden">
                                                                <Image 
                                                                    src={productInfo.images[0].url} 
                                                                    alt={productInfo.name}
                                                                    size="sm"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className="max-sm:hidden w-8 h-8 bg-gray-200 flex items-center justify-center rounded">
                                                                <ImageIcon className="w-4 h-4" />
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="font-medium">{productInfo.name}</div>
                                                            <div className="text-sm text-gray-500">{productInfo.sku}</div>
                                                        </div>
                                                    </Link>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </TableListCell>
                                        )}
                                        <TableListCell><ActionBadge type={action.type as string} amount={action.amount} /></TableListCell>
                                        <TableListCell>
                                            <DateTime timestamp={action.keyHistory?.createdAt} hideTimeOnSm hideTimeOnMd />
                                        </TableListCell>
                                        {showTransactionColumn && <TableListCell>
                                            {action.transaction?.transactionId ? (
                                                <Link to={`/storage/transactions/${action.transaction.transactionId}`} onClick={(e) => e.stopPropagation()}>
                                                    <Tag colorScheme="blue">#{action.transaction.transactionId}</Tag>
                                                </Link>
                                            ) : '-'}
                                        </TableListCell>}
                                        <TableListCell hideBelowLg>
                                            <UserCard userId={action.createdBy} size="small" />
                                        </TableListCell>
                                        <TableListCell>
                                            <div className="flex items-center gap-2">
                                                {action.isDraft && <Tag colorScheme="yellow">{t('tagDraft')}</Tag>}
                                                {action.isDraft && action.id && (action.ids?.productId || productId) && limitToRoles([AccessRole.OWNER, AccessRole.EDITOR, AccessRole.WORKER]) && (
                                                    <div onClick={(event) => event.stopPropagation()}>
                                                        <IconButton
                                                            icon={<DeleteIcon />}
                                                            size="small"
                                                            colorScheme="red"
                                                            onClick={() => openDialog('delete_action', {
                                                                storageId: action.ids?.storageId || storageId,
                                                                productId: action.ids?.productId || productId,
                                                                actionId: action.id,
                                                                transactionDocId: action.transaction?.id,
                                                            })}
                                                        />
                                                    </div>
                                                )}
                                                {action.id && (action.ids?.productId || productId) && (
                                                    <div onClick={(event) => event.stopPropagation()}>
                                                        <IconButton
                                                            icon={<HistoryIcon />}
                                                            size="small"
                                                            colorScheme="blue"
                                                            onClick={() => openDialog('action_history', {
                                                                storageId: action.ids?.storageId || storageId,
                                                                productId: action.ids?.productId || productId,
                                                                actionId: action.id,
                                                            })}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </TableListCell>
                                    </TableListRow>
                                );
                            })
                            : Array.from({ length: 3 }).map((_, i) => (
                                <TableListRow key={i}>
                                    {actionPaths && <TableListCell><div className="skeleton"></div></TableListCell>}
                                    <TableListCell><div className="skeleton"></div></TableListCell>
                                    <TableListCell><div className="skeleton"></div></TableListCell>
                                    {showTransactionColumn && <TableListCell><div className="skeleton"></div></TableListCell>}
                                    <TableListCell hideBelowLg><Avatar isLoading /></TableListCell>
                                    <TableListCell><div className="skeleton"></div></TableListCell>
                                </TableListRow>
                            ))
                        }
                    </TableList>
                </div>
            </DocList>
            <LoadMore hasNextPage={hasNextPage} action={fetchNextPage} isLoading={actionsStatus === FormStatus.LOADING} trigger={filteredActions} />
        </>
    );
}

export default ActionsList;

